import React from 'react';
import { Link } from 'react-router-dom';
import { ResponsiveTabs, SideNavTabs } from '../../../Components/Common/DashboardKit';
import HrEmbeddedPage from './HrEmbeddedPage';

/**
 * Leave — all FTO and COA work in one understandable area.
 * (Round: HR UI Simplification, Phase 1 — composition only.)
 *
 * "FTO Central" and "COA Central" stop being unrelated top-level
 * applications: their parent is now Leave. Internal page titles keep
 * their names — only the navigation above them changed.
 *
 * Areas (secondary selector):
 *   Requests   → compact panel LEADING to the existing review/approval
 *                functionality (Review queue + classic workbench). The
 *                queue is NOT mounted a second time here and no second
 *                pending counter is shown — one queue, one counter.
 *   FTO admin  → side list of the existing FTO admin pages, chosen one
 *                renders in place (lazy): Summary, Add paid FTO, Add
 *                FTO (paid/unpaid), Holidays, Year-end, Year-end
 *                requests, Comp days.
 *   COA admin  → the existing COA Central page in place.
 *   History    → the existing Leave Archive (with its void/reversal
 *                workflow) in place.
 * Plus the prominent "Record absence" action (the existing 3-step
 * wizard) — kept front and center per the spec.
 *
 * Every page below is the SAME component its old deep route rendered —
 * unchanged fetches, payloads, validations, confirmations. Deep routes
 * now redirect here (see allRoutes.js), so old bookmarks land on the
 * same tool inside the shell.
 */
const LazyRecord   = React.lazy(() => import('../RecordAbsence'));
const LazyCoa      = React.lazy(() => import('../CoaCentral'));
const LazyArchive  = React.lazy(() => import('../LeaveArchive'));
const LazySummary  = React.lazy(() => import('../FtoHrSummary'));
const LazyPaidFto  = React.lazy(() => import('../AddPaidFto'));
const LazyAddFto   = React.lazy(() => import('../AddFtoHr'));
const LazyHolidays = React.lazy(() => import('../FtoHolidays'));
const LazyYearEnd  = React.lazy(() => import('../FtoYearEnd'));
const LazyYeReq    = React.lazy(() => import('../YearEndRequestView'));
const LazyCompDays = React.lazy(() => import('../CompensationDays'));

const AREAS = [
    { key: 'requests', label: 'Requests',           icon: 'ri-inbox-unarchive-line' },
    { key: 'fto',      label: 'FTO administration', icon: 'ri-calendar-2-line' },
    { key: 'coa',      label: 'COA administration', icon: 'ri-calendar-check-line' },
    { key: 'history',  label: 'History',            icon: 'ri-archive-line' },
];

const FTO_ITEMS = [
    { key: 'summary',          label: 'FTO HR summary',    icon: 'ri-table-line',          Comp: LazySummary },
    { key: 'record-paid',      label: 'Add paid FTO',      icon: 'ri-cash-line',           Comp: LazyPaidFto },
    { key: 'add-fto',          label: 'Add FTO (paid/unpaid)', icon: 'ri-calendar-close-line', Comp: LazyAddFto },
    { key: 'holidays',         label: 'FTO holidays',      icon: 'ri-calendar-event-line', Comp: LazyHolidays },
    { key: 'year-end',         label: 'Year-end FTO',      icon: 'ri-refresh-line',        Comp: LazyYearEnd },
    { key: 'year-end-request', label: 'Year-end requests', icon: 'ri-file-list-2-line',    Comp: LazyYeReq },
    { key: 'comp-days',        label: 'Compensation days', icon: 'ri-gift-line',           Comp: LazyCompDays },
];

const HrLeaveSection = ({ area, item, onAreaChange, onItemChange }) => {
    const activeArea = area === 'record' || AREAS.some((a) => a.key === area) ? area : 'requests';
    const activeItem = FTO_ITEMS.find((i) => i.key === item) || FTO_ITEMS[0];
    const FtoComp = activeItem.Comp;

    return (
        <div>
            <div className="hr-section-title">
                <div>
                    <h5>Leave</h5>
                    <span className="text-muted">FTO and COA — requests, administration, and history.</span>
                </div>
                {activeArea !== 'record' && (
                    <button type="button" className="btn btn-success"
                        onClick={() => onAreaChange('record')}>
                        <i className="ri-calendar-todo-line align-bottom me-1" aria-hidden="true" />
                        Record absence
                    </button>
                )}
            </div>

            {activeArea === 'record' ? (
                <div>
                    <button type="button" className="btn btn-light btn-sm mb-2" onClick={() => onAreaChange('requests')}>
                        <i className="ri-arrow-left-line align-bottom me-1" aria-hidden="true" />Back to Leave
                    </button>
                    <HrEmbeddedPage><LazyRecord /></HrEmbeddedPage>
                </div>
            ) : (
                <>
                    <ResponsiveTabs
                        tabs={AREAS}
                        activeKey={activeArea}
                        onChange={onAreaChange}
                        variant="pills"
                        size="sm"
                    />

                    {activeArea === 'requests' && (
                        <div className="py-2">
                            <p className="text-muted mb-2" style={{ maxWidth: 720 }}>
                                Pending FTO and COA requests are decided in the Review queue — one
                                queue for every pending item, so nothing gets missed.
                            </p>
                            <div className="d-flex flex-wrap gap-2">
                                <Link to="/humanresource?section=review" className="btn btn-primary">
                                    <i className="ri-inbox-unarchive-line align-bottom me-1" aria-hidden="true" />
                                    Open Review queue
                                </Link>
                                <Link to="/attendance/approvals" className="btn btn-soft-primary">
                                    Classic FTO/COA workbench
                                </Link>
                            </div>
                        </div>
                    )}

                    {activeArea === 'fto' && (
                        <div className="hr-sidegrid">
                            <SideNavTabs
                                tabs={FTO_ITEMS.map((i) => ({ key: i.key, label: i.label, icon: i.icon }))}
                                activeKey={activeItem.key}
                                onChange={onItemChange}
                                width={220}
                                ariaLabel="FTO administration tools"
                            />
                            <HrEmbeddedPage><FtoComp /></HrEmbeddedPage>
                        </div>
                    )}

                    {activeArea === 'coa' && <HrEmbeddedPage><LazyCoa /></HrEmbeddedPage>}
                    {activeArea === 'history' && <HrEmbeddedPage><LazyArchive /></HrEmbeddedPage>}
                </>
            )}
        </div>
    );
};

export default HrLeaveSection;
